/**
 * Date & Time Formatting Utilities
 * Uses global settings from localStorage for consistent formatting across the app
 */

// Default settings (fallback if not loaded from API)
const DEFAULT_SETTINGS = {
  timezone: 'Asia/Kolkata',
  date_format: 'DD/MM/YYYY',
  time_format: '12-hour',
  week_start_day: 'Monday',
};

/**
 * Get current timezone settings from localStorage
 */
export const getTimezoneSettings = () => {
  try {
    const settings = localStorage.getItem('timezoneSettings');
    return settings ? JSON.parse(settings) : DEFAULT_SETTINGS;
  } catch {
    return DEFAULT_SETTINGS;
  }
};

/**
 * Save timezone settings to localStorage (call after fetching from API)
 */
export const saveTimezoneSettings = (settings) => {
  try {
    localStorage.setItem('timezoneSettings', JSON.stringify(settings));
  } catch (error) {
    console.error('Failed to save timezone settings:', error);
  }
};

/**
 * Format date according to user's preferred format
 * @param {string|Date} dateInput - Date string or Date object
 * @param {string} format - Optional override format (DD/MM/YYYY, MM/DD/YYYY, YYYY-MM-DD, etc.)
 * @returns {string} Formatted date string
 */
export const formatDate = (dateInput, format = null) => {
  if (!dateInput) return '-';
  
  try {
    const settings = getTimezoneSettings();
    const dateFormat = format || settings.date_format || 'DD/MM/YYYY';
    
    let date;
    if (typeof dateInput === 'string') {
      // Handle ISO string or date-only string
      date = new Date(dateInput.includes('T') ? dateInput : dateInput + 'T00:00:00');
    } else if (typeof dateInput === 'number') {
      date = new Date(dateInput);
    } else {
      date = dateInput;
    }
    
    if (isNaN(date.getTime())) return dateInput;
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthNameShort = monthNames[date.getMonth()];
    
    switch (dateFormat) {
      case 'DD/MM/YYYY':
        return `${day}/${month}/${year}`;
      case 'MM/DD/YYYY':
        return `${month}/${day}/${year}`;
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`;
      case 'DD-Mon-YYYY':
        return `${day}-${monthNameShort}-${year}`;
      case 'DD MMM YYYY':
        return `${day} ${monthNameShort} ${year}`;
      default:
        return `${day}/${month}/${year}`;
    }
  } catch {
    return dateInput?.toString() || '-';
  }
};

/**
 * Format time according to user's preferred format
 * @param {string|Date} timeInput - Time string (HH:mm or ISO) or Date object
 * @param {string} format - Optional override format ('12-hour' or '24-hour')
 * @returns {string} Formatted time string
 */
export const formatTime = (timeInput, format = null) => {
  if (!timeInput) return '-';
  
  try {
    const settings = getTimezoneSettings();
    const timeFormat = format || settings.time_format || '12-hour';
    
    let date;
    if (typeof timeInput === 'string') {
      if (timeInput.includes('T')) {
        date = new Date(timeInput);
      } else if (timeInput.includes(':')) {
        // Time only string like "14:30"
        const [hours, minutes] = timeInput.split(':').map(Number);
        date = new Date();
        date.setHours(hours, minutes, 0, 0);
      } else {
        return timeInput;
      }
    } else {
      date = timeInput;
    }
    
    if (isNaN(date.getTime())) return timeInput;
    
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    if (timeFormat === '24-hour') {
      return `${hours.toString().padStart(2, '0')}:${minutes}`;
    } else {
      // 12-hour format
      const period = hours >= 12 ? 'PM' : 'AM';
      const hours12 = hours % 12 || 12;
      return `${hours12}:${minutes} ${period}`;
    }
  } catch {
    return timeInput?.toString() || '-';
  }
};

/**
 * Format datetime with both date and time
 * @param {string|Date} dateTimeInput - DateTime string or Date object
 * @param {object} options - { dateFormat, timeFormat }
 * @returns {string} Formatted datetime string
 */
export const formatDateTime = (dateTimeInput, options = {}) => {
  if (!dateTimeInput) return '-';
  
  const dateStr = formatDate(dateTimeInput, options.dateFormat);
  const timeStr = formatTime(dateTimeInput, options.timeFormat);
  
  return `${dateStr} ${timeStr}`;
};

/**
 * Get current time in the configured timezone
 * @returns {Date} Current date/time
 */
export const getCurrentTime = () => {
  const settings = getTimezoneSettings();
  
  try {
    const options = { timeZone: settings.timezone };
    const dateString = new Date().toLocaleString('en-US', options);
    return new Date(dateString);
  } catch {
    return new Date();
  }
};

/**
 * Get formatted current date
 * @returns {string} Current date in user's preferred format
 */
export const getCurrentFormattedDate = () => {
  return formatDate(new Date());
};

/**
 * Get formatted current time
 * @returns {string} Current time in user's preferred format
 */
export const getCurrentFormattedTime = () => {
  return formatTime(new Date());
};

/**
 * Parse a date string to Date object (timezone aware)
 * @param {string} dateString - Date string to parse
 * @returns {Date} Date object
 */
export const parseDate = (dateString) => {
  if (!dateString) return null;
  
  try {
    // Handle different formats
    if (dateString.includes('T')) {
      return new Date(dateString);
    }
    
    // Try DD/MM/YYYY
    const ddmmyyyy = dateString.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (ddmmyyyy) {
      return new Date(ddmmyyyy[3], ddmmyyyy[2] - 1, ddmmyyyy[1]);
    }
    
    // Try MM/DD/YYYY
    const mmddyyyy = dateString.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (mmddyyyy) {
      return new Date(mmddyyyy[3], mmddyyyy[1] - 1, mmddyyyy[2]);
    }
    
    // Try YYYY-MM-DD
    const isoDate = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoDate) {
      return new Date(isoDate[1], isoDate[2] - 1, isoDate[3]);
    }
    
    return new Date(dateString);
  } catch {
    return null;
  }
};

/**
 * Get relative time string (e.g., "2 hours ago", "in 3 days")
 * @param {string|Date} dateInput - Date to compare
 * @returns {string} Relative time string
 */
export const getRelativeTime = (dateInput) => {
  if (!dateInput) return '-';
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHours = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMs < 0) {
      // Future
      if (Math.abs(diffDays) > 1) return `in ${Math.abs(diffDays)} days`;
      if (Math.abs(diffHours) > 1) return `in ${Math.abs(diffHours)} hours`;
      if (Math.abs(diffMin) > 1) return `in ${Math.abs(diffMin)} minutes`;
      return 'just now';
    } else {
      // Past
      if (diffDays > 30) return formatDate(date);
      if (diffDays > 1) return `${diffDays} days ago`;
      if (diffDays === 1) return 'yesterday';
      if (diffHours > 1) return `${diffHours} hours ago`;
      if (diffMin > 1) return `${diffMin} minutes ago`;
      return 'just now';
    }
  } catch {
    return '-';
  }
};

export default {
  getTimezoneSettings,
  saveTimezoneSettings,
  formatDate,
  formatTime,
  formatDateTime,
  getCurrentTime,
  getCurrentFormattedDate,
  getCurrentFormattedTime,
  parseDate,
  getRelativeTime,
};
